import { css, cx } from "@emotion/css";
import { Callout, resetStyles } from "@octopusdeploy/design-system-components";
import { CloudArrowUpIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import type { FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import DeletableChip from "~/components/Chips/DeletableChip";
import { useErrorActions } from "~/components/ErrorContext/ErrorContext";
interface FileUploadDropzoneProps {
    onFilesChanged: (newValue: File[]) => void;
    label: string;
    filesLimit?: number;
    showFileChips?: boolean;
}
function FileUploadDropzone({ onFilesChanged, label, filesLimit = 1, showFileChips = true }: FileUploadDropzoneProps) {
    const MAX_SUPPORTED_FILE_SIZE = 1000000000000; // 1TB in bytes
    const { clearErrors } = useErrorActions();
    const [files, setFiles] = React.useState<File[]>([]);
    const [error, setError] = React.useState<string | undefined>();
    const fileChipsHeaderText = filesLimit > 1 ? "Selected files" : "Selected file";
    const handleChange = (files: File[]) => {
        clearErrors();
        setFiles(files);
        onFilesChanged(files);
    };
    const handleDelete = (deletedFile: File) => {
        const updatedFiles = files.filter((file) => file !== deletedFile);
        handleChange(updatedFiles);
    };
    const handleDropAccepted = async (acceptedFiles: File[]) => {
        if (filesLimit > 1 && files.length + acceptedFiles.length > filesLimit) {
            setError(`Maximum allowed number of files exceeded. Only ${filesLimit} allowed`);
            return;
        }
        setError(undefined);
        handleChange(filesLimit <= 1 ? acceptedFiles : [...files, ...acceptedFiles]);
    };
    const handleDropRejected = (rejectedFiles: FileRejection[]) => {
        if (rejectedFiles.length > filesLimit) {
            setError(`Maximum allowed number of files exceeded. Only ${filesLimit} allowed`);
        }
        else {
            let message = "";
            const maxFileSizeInGb = MAX_SUPPORTED_FILE_SIZE / 1000000000 + " GB";
            rejectedFiles.forEach((rejectedFile) => {
                if (rejectedFile.file.size > MAX_SUPPORTED_FILE_SIZE) {
                    message = `File ${rejectedFile.file.name} is too big. Size limit is ` + maxFileSizeInGb + ". To upload a larger file we recommend using the Octopus CLI";
                }
                else {
                    message = `File ${rejectedFile.file.name} was rejected. `;
                }
            });
            setError(message);
        }
    };
    const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({ onDropAccepted: handleDropAccepted, onDropRejected: handleDropRejected, maxSize: MAX_SUPPORTED_FILE_SIZE, maxFiles: filesLimit, multiple: filesLimit > 1 });
    return (<div className={styles.root}>
            <div {...getRootProps({
        className: cx(styles.dropzone, {
            [styles.rootActive]: isDragActive,
            [styles.rootReject]: isDragActive && isDragReject,
        }),
    })}>
                <input {...getInputProps()}/>
                <CloudArrowUpIcon size={24}/>
                <span className={styles.label}>{label}</span>
            </div>
            {error && (<Callout hideTitle type="danger">
                    {error}
                </Callout>)}
            {showFileChips && files.length > 0 && (<>
                    <h3 className={styles.fileChipsHeader}>{fileChipsHeaderText}</h3>
                    <div className={styles.fileChipsList}>
                        {files.map((file, index) => (<DeletableChip key={index} onRequestDelete={() => handleDelete(file)} deleteButtonAccessibleName={`Delete ${file.name}`} noMargin>
                                {file.name}
                            </DeletableChip>))}
                    </div>
                </>)}
        </div>);
}
export default FileUploadDropzone;
const styles = {
    root: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
    }),
    dropzone: css({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: space[8],
        cursor: "pointer",
        border: "dashed",
        borderColor: themeTokens.color.border.primary,
        borderRadius: borderRadius.small,
        backgroundColor: themeTokens.color.background.secondary.default,
        minHeight: "10rem",
        color: themeTokens.color.text.secondary,
    }),
    rootActive: css({
        border: "solid",
        backgroundColor: themeTokens.color.background.secondary.default,
        borderColor: themeTokens.color.border.info,
    }),
    rootReject: css({
        border: "solid",
        backgroundColor: themeTokens.color.background.danger,
        borderColor: themeTokens.color.border.danger,
    }),
    label: css({
        text: text.body.regular.medium,
        maxWidth: "23rem",
        textAlign: "center",
    }),
    fileChipsHeader: css({
        ...resetStyles.heading,
        font: text.body.regular.large,
        marginTop: space[8],
    }),
    fileChipsList: css({
        display: "flex",
        padding: space[4],
        gap: space[8],
        flexWrap: "wrap",
    }),
};
